<template>
  <!-- Wears ModalDialogForm, the card the app-wide ModalDialog renders, so the
       prompt reads as one of the app's own dialogs: same width, title, message
       type and button pair. It is not routed THROUGH ModalDialog because that
       dialog is store-driven and single-fire, and this one must stay open
       across a cancelled passkey ceremony. -->
  <v-dialog
    v-model="isShowing"
    :max-width="maxWidth"
    persistent
    @keydown.esc="!isCreating && notNow()"
  >
    <ModalDialogForm
      title="Create a passkey"
      message="Next time, sign in with your fingerprint, face or device PIN instead of your password. It takes a few seconds, and you can manage your passkeys from the menu under your initials."
      ok-button-caption-verb-override="Create a passkey"
      cancel-button-caption-verb-override="Not now"
      :ok-button-width="200"
      :do-skip-verification="true"
      :max-width="maxWidth"
      :is-showing="isShowing"
      :is-processing="isCreating"
      @ok="createPasskey"
      @cancel="notNow"
    />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import { getPasskeys, registerPasskey } from '~/services/passkeys'
import { PasskeysUnavailableHereError, ServiceError } from '~/services/errors'
import { addNotification } from '~/cynqueModules/addNotification'
import {
  dismissPasskeyNudge,
  takePasskeyNudge,
} from '~/cynqueModules/passkeyNudge'
import { describeWebAuthnError } from '~/cynqueModules/webAuthnErrors'
import { suggestPasskeyLabel } from '~/cynqueModules/suggestPasskeyLabel'

const mn = '[components/passkeys/PasskeyNudgeDialog.vue]'

/**
 * After a password sign-in, offer to create a passkey — but only to a user
 * who has none, on a browser that can make one. The login page asks for the
 * prompt (cynqueModules/passkeyNudge.ts); this decides.
 */
export default defineComponent({
  data() {
    return {
      isShowing: false,
      isCreating: false,
      maxWidth: 580,
    }
  },
  async mounted() {
    const fn = `${mn} mounted |`
    if (!takePasskeyNudge() || !browserSupportsWebAuthn()) {
      return
    }
    try {
      const passkeys = await getPasskeys()
      this.isShowing = passkeys.length === 0
    } catch (e) {
      // A prompt is not worth an error message.
      console.debug(`${fn} Not prompting: could not list passkeys.`, e)
    }
  },
  methods: {
    notNow() {
      dismissPasskeyNudge()
      this.isShowing = false
    },
    async createPasskey() {
      this.isCreating = true
      try {
        const passkey = await registerPasskey(suggestPasskeyLabel())
        this.isShowing = false
        addNotification({
          type: 'success',
          message: `Passkey "${passkey.label}" created. Use it next time you sign in.`,
        })
      } catch (e) {
        if (e instanceof PasskeysUnavailableHereError) {
          // Nothing the user can do about it here; don't offer it again.
          dismissPasskeyNudge()
          this.isShowing = false
        }
        // A cancel says nothing and leaves the prompt up, so "Not now" is one
        // click away.
        const message = describeWebAuthnError(e, 'create')
        if (message) {
          addNotification({
            type: 'warning',
            message,
            statusCode:
              e instanceof ServiceError && e.statusCode
                ? e.statusCode
                : undefined,
          })
        }
      } finally {
        this.isCreating = false
      }
    },
  },
})
</script>
